/* eslint-disable max-lines */
import { useCallback, useRef, useState } from 'react';

import {
   appendTestableComponentAttributes,
   Button,
   ButtonVariant,
   Modal,
   ModalCloseButton,
   ModalConfirmButton,
   ModalMaxHeight,
   ModalWidth,
   StyledFlex,
} from '@dreryk/react-components';
import { useTranslate } from '@dreryk/react-common';

import { translationKeys } from '../translations';
import type { Field } from '../types';

import { AcceptButton } from './AcceptRejectButtons';
import {
   StyledFieldProposalCard,
   StyledFieldProposalCardHeader,
   StyledFieldProposalCardLabel,
   StyledFieldProposalCardStatus,
   StyledFieldProposalTextarea,
   StyledVerificationBadge,
} from './FieldProposalReviewModal.styled';
// eslint-disable-next-line no-duplicate-imports
import type { AssignableFieldKey, FieldCardVariant, FieldKey, FieldStatus } from './FieldProposalReviewModal.types';
// eslint-disable-next-line no-duplicate-imports
import { ASSIGNABLE_FIELDS, FIELD_TO_VISIT_FIELD } from './FieldProposalReviewModal.types';
import { RedactionProposal } from './RedactionProposal';
import { useFieldRedaction } from './useFieldRedaction';

// ─── Translation key map ──────────────────────────────────────────────────────

const FIELD_TRANSLATION_KEYS: Record<FieldKey, string> = {
   interview: translationKeys.fieldProposalReview.fieldInterview,
   examination: translationKeys.fieldProposalReview.fieldExamination,
   recommendations: translationKeys.fieldProposalReview.fieldRecommendations,
   unassigned: translationKeys.fieldProposalReview.fieldUnassigned,
};

// ─── Field card action bar ────────────────────────────────────────────────────

type FieldCardActionsProps = {
   variant: FieldCardVariant;
   // eslint-disable-next-line react/no-unused-prop-types
   field: FieldKey;
   text: string;
   isRedacting: boolean;
   onAccept?: (field: AssignableFieldKey) => void;
   onRedact: VoidFunction;
};

const FieldCardActions = (props: FieldCardActionsProps): JSX.Element => (
   <StyledFlex direction={'row'} gap={6} alignItems={'center'}>
      {props.variant === 'assignable' && props.isRedacting === false ? (
         <AcceptButton
            testId={`fieldProposalAccept-${props.field}`}
            onClick={() => props.onAccept?.(props.field as AssignableFieldKey)}
         />
      ) : null}
      <Button
         testId={`fieldProposalRedact-${props.field}`}
         label={translationKeys.panel.redactButton}
         onClick={props.onRedact}
         variant={ButtonVariant.SECONDARY}
         disabled={props.isRedacting || !props.text.trim()}
      />
   </StyledFlex>
);

// ─── Assign-to row (unassigned variant only) ──────────────────────────────────

type AssignToRowProps = {
   getSelectedText: () => string;
   getFullText: () => string;
   onAssign: (targetField: AssignableFieldKey, text: string) => void;
};

const AssignToRow = (props: AssignToRowProps): JSX.Element => {
   const translate = useTranslate();
   return (
      <StyledFlex direction={'column'} gap={6}>
         <StyledFieldProposalCardLabel>
            {translate(translationKeys.fieldProposalReview.assignTo)}
         </StyledFieldProposalCardLabel>
         <StyledFlex direction={'row'} gap={6}>
            {ASSIGNABLE_FIELDS.map((targetField) => (
               <Button
                  key={targetField}
                  testId={`fieldProposalAssign-unassigned-to-${targetField}`}
                  label={FIELD_TRANSLATION_KEYS[targetField]}
                  onClick={() => {
                     const selected = props.getSelectedText();
                     props.onAssign(targetField, selected || props.getFullText());
                  }}
                  variant={ButtonVariant.SECONDARY}
               />
            ))}
         </StyledFlex>
      </StyledFlex>
   );
};

// ─── Unified field card ───────────────────────────────────────────────────────

type FieldCardProps = {
   variant: FieldCardVariant;
   field: FieldKey;
   text: string;
   status: FieldStatus;
   onTextChange: (text: string) => void;
   onAccept?: (field: AssignableFieldKey) => void;
   onReject: (field: FieldKey) => void;
   onAssign?: (targetField: AssignableFieldKey, text: string) => void;
};

// eslint-disable-next-line max-lines-per-function
export const FieldCard = (props: FieldCardProps): JSX.Element => {
   const translate = useTranslate();
   const textRef = useRef(props.text);
   textRef.current = props.text;

   const getFullText = useCallback(() => textRef.current, []);

   const redaction = useFieldRedaction({
      field: props.field,
      getText: getFullText,
      onRedacted: props.onTextChange,
   });

   const isActioned = props.status !== 'pending';
   const isRedacting = redaction.redactionState !== 'idle' && redaction.redactionState !== 'error';

   const getSelectedText = useCallback((): string => {
      const el = redaction.textareaRef.current;
      if (!el) {
         return '';
      }
      const { selectionStart, selectionEnd } = el;
      return selectionStart === selectionEnd ? '' : el.value.slice(selectionStart, selectionEnd);
   }, [redaction.textareaRef]);

   return (
      <StyledFieldProposalCard {...appendTestableComponentAttributes('FieldCard', `fieldProposalCard-${props.field}`)}>
         <StyledFieldProposalCardHeader>
            <StyledFieldProposalCardLabel>
               {translate(FIELD_TRANSLATION_KEYS[props.field])}
            </StyledFieldProposalCardLabel>
            {isActioned ? (
               <StyledFieldProposalCardStatus $status={props.status}>
                  {props.status === 'accepted'
                     ? translate(translationKeys.fieldProposalReview.accepted)
                     : translate(translationKeys.fieldProposalReview.rejected)}
               </StyledFieldProposalCardStatus>
            ) : (
               <FieldCardActions
                  variant={props.variant}
                  field={props.field}
                  text={props.text}
                  isRedacting={isRedacting}
                  onAccept={props.onAccept}
                  onRedact={redaction.redact}
               />
            )}
         </StyledFieldProposalCardHeader>

         <StyledFieldProposalTextarea
            ref={redaction.textareaRef}
            value={props.text}
            onChange={(event) => props.onTextChange(event.target.value)}
            disabled={isActioned || isRedacting}
            rows={4}
            data-testid={`fieldProposalTextarea-${props.field}`}
         />

         {redaction.redactionState === 'proposal' && redaction.redactedText && redaction.originalText ? (
            <RedactionProposal
               originalText={redaction.originalText}
               redactedText={redaction.redactedText}
               onAccept={redaction.accept}
               onReject={redaction.reject}
               onRetry={redaction.retry}
            />
         ) : null}

         {props.variant === 'unassigned' && !isActioned && !isRedacting ? (
            <AssignToRow
               getSelectedText={getSelectedText}
               getFullText={getFullText}
               onAssign={(targetField, text) => props.onAssign?.(targetField, text)}
            />
         ) : null}
      </StyledFieldProposalCard>
   );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

type FieldProposalReviewModalProps = {
   interview: string;
   examination: string;
   recommendations: string;
   unassigned?: string;
   onClose: VoidFunction;
   onAppendToField?: (field: Field, text: string) => void;
};

// eslint-disable-next-line max-lines-per-function,max-statements
export const FieldProposalReviewModal = (props: FieldProposalReviewModalProps): JSX.Element => {
   const translate = useTranslate();

   const [texts, setTexts] = useState({
      interview: props.interview,
      examination: props.examination,
      recommendations: props.recommendations,
      unassigned: props.unassigned ?? '',
   });

   const [statuses, setStatuses] = useState<Record<FieldKey, FieldStatus>>({
      interview: 'pending',
      examination: 'pending',
      recommendations: 'pending',
      unassigned: 'pending',
   });

   const handleTextChange = useCallback((field: FieldKey, text: string) => {
      setTexts((prev) => ({ ...prev, [field]: text }));
   }, []);

   const handleAccept = useCallback(
      (field: AssignableFieldKey) => {
         const text = texts[field].trim();
         if (text && props.onAppendToField) {
            props.onAppendToField(FIELD_TO_VISIT_FIELD[field], text);
         }
         setStatuses((prev) => ({ ...prev, [field]: 'accepted' }));
      },
      [texts, props]
   );

   const handleAcceptAll = useCallback(() => {
      ASSIGNABLE_FIELDS.forEach((field) => {
         if (statuses[field] !== 'pending') {
            return;
         }
         const text = texts[field].trim();
         if (text && props.onAppendToField) {
            props.onAppendToField(FIELD_TO_VISIT_FIELD[field], text);
         }
      });
      setStatuses((prev) => ({
         ...prev,
         interview: prev.interview === 'pending' ? 'accepted' : prev.interview,
         examination: prev.examination === 'pending' ? 'accepted' : prev.examination,
         recommendations: prev.recommendations === 'pending' ? 'accepted' : prev.recommendations,
      }));
   }, [texts, statuses, props]);

   const handleReject = useCallback((field: FieldKey) => {
      setStatuses((prev) => ({ ...prev, [field]: 'rejected' }));
   }, []);

   const handleAssignUnassigned = useCallback(
      (targetField: AssignableFieldKey, text: string) => {
         if (text.trim() && props.onAppendToField) {
            props.onAppendToField(FIELD_TO_VISIT_FIELD[targetField], text.trim());
         }
         setStatuses((prev) => ({ ...prev, unassigned: 'accepted' }));
      },
      [props]
   );

   const hasUnacceptedAssignableField =
      statuses.interview === 'pending' ||
      statuses.examination === 'pending' ||
      statuses.recommendations === 'pending' ||
      (props.unassigned !== undefined && props.unassigned.length > 0 && statuses.unassigned === 'pending');

   const allAssignableActioned =
      statuses.interview !== 'pending' && statuses.examination !== 'pending' && statuses.recommendations !== 'pending';

   return (
      <Modal
         testId={'fieldProposalReviewModal'}
         title={
            <StyledFlex direction={'row'} gap={8} alignItems={'center'}>
               {translate(translationKeys.fieldProposalReview.title)}
               <StyledVerificationBadge>
                  {translate(translationKeys.fieldProposalReview.verificationRequired)}
               </StyledVerificationBadge>
            </StyledFlex>
         }
         height={ModalMaxHeight.FULL}
         width={ModalWidth.WIDE}
         shouldCloseOnEsc
      >
         <StyledFlex direction={'column'} gap={12}>
            {ASSIGNABLE_FIELDS.map((field) => (
               <FieldCard
                  key={field}
                  variant={'assignable'}
                  field={field}
                  text={texts[field]}
                  status={statuses[field]}
                  onTextChange={(text) => handleTextChange(field, text)}
                  onAccept={handleAccept}
                  onReject={handleReject}
               />
            ))}
            {props.unassigned ? (
               <FieldCard
                  variant={'unassigned'}
                  field={'unassigned'}
                  text={texts.unassigned}
                  status={statuses.unassigned}
                  onTextChange={(text) => handleTextChange('unassigned', text)}
                  onReject={handleReject}
                  onAssign={handleAssignUnassigned}
               />
            ) : null}
         </StyledFlex>
         <ModalCloseButton onClick={props.onClose} shouldConfirmExit={hasUnacceptedAssignableField} />
         <ModalConfirmButton
            label={translationKeys.fieldProposalReview.acceptAll}
            onClick={handleAcceptAll}
            disabled={allAssignableActioned}
         />
      </Modal>
   );
};
